import { useState, useEffect, useRef } from 'react';
import { BackupItem, BackupConfig, RestoreOptions } from '../types';
import { BackupAPI } from '../api';

export const useBackupOperations = () => {
  const [loading, setLoading] = useState(false);
  const [selectedBackups, setSelectedBackups] = useState<string[]>([]);
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [backupConfig, setBackupConfig] = useState<BackupConfig>({
    enabled: true,
    driver: 'local',
    retentionDays: 30,
    maxBackupsPerZone: 10,
    compressionLevel: 6,
    autoBackup: true,
  });
  
  const [job, setJob] = useState<{
    id: string;
    status: string;
    total: number;
    completed: number;
    errors: string[];
  } | null>(null);
  
  const jobPollRef = useRef<any>(null);

  const fetchBackups = async () => {
    try {
      const data = await BackupAPI.fetchBackups();
      setBackups(data);
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    }
  };

  const fetchBackupConfig = async () => {
    try {
      const data = await BackupAPI.fetchBackupConfig();
      setBackupConfig(data);
    } catch (error) {
      console.error('Failed to fetch backup config:', error);
    }
  };

  const saveBackupConfig = async () => {
    setLoading(true);
    try {
      await BackupAPI.saveBackupConfig(backupConfig);
    } catch (error) {
      console.error('Failed to save backup config:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollJob = async (jobId: string) => {
    try {
      const res = await fetch(`/api/backups/jobs/${jobId}`);
      if (!res.ok) return;
      const data = await res.json();
      setJob(data);
      if (data.status === 'completed' || data.status === 'failed') {
        if (jobPollRef.current) clearInterval(jobPollRef.current);
        jobPollRef.current = null;
        await fetchBackups();
        setLoading(false);
      }
    } catch {
      // Silent fail for polling errors
    }
  };

  const createBackup = async (zoneId?: string) => {
    setLoading(true);
    try {
      const res = await BackupAPI.createBackup(zoneId);
      if (res?.jobId) {
        setJob({ id: res.jobId, status: 'running', total: 0, completed: 0, errors: [] });
        jobPollRef.current = setInterval(() => pollJob(res.jobId), 1500);
      } else {
        await fetchBackups();
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to create backup:', error);
      setLoading(false);
    }
  };

  const restoreBackup = async (backupId: string, options: RestoreOptions) => {
    setLoading(true);
    try {
      await BackupAPI.restoreBackup(backupId, options);
    } catch (error) {
      console.error('Failed to restore backup:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackupSelect = (backupId: string) => {
    setSelectedBackups((prev) =>
      prev.includes(backupId) ? prev.filter((id) => id !== backupId) : [...prev, backupId],
    );
  };

  const handleBulkAction = async (action: string) => {
    setLoading(true);
    try {
      if (action === 'download') {
        for (const backupId of selectedBackups) {
          await BackupAPI.downloadBackup(backupId);
        }
      } else if (action === 'delete') {
        await Promise.all(selectedBackups.map((id) => BackupAPI.deleteBackup(id)));
        await fetchBackups();
      }
      setSelectedBackups([]);
    } catch (error) {
      console.error(`Failed to ${action} backups:`, error);
    } finally {
      setLoading(false);
    }
  };

  const hideJob = () => {
    if (jobPollRef.current) clearInterval(jobPollRef.current);
    jobPollRef.current = null;
    setJob(null);
    setLoading(false);
  };

  useEffect(() => {
    fetchBackups();
    fetchBackupConfig();
  }, []);

  return {
    loading,
    selectedBackups,
    backups,
    backupConfig,
    job,
    setBackupConfig,
    fetchBackups,
    saveBackupConfig,
    createBackup,
    restoreBackup,
    handleBackupSelect,
    handleBulkAction,
    hideJob,
  };
};